/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 예외처리를 위한 클래스를 정의한다.
 *
 * @file /src/utils/Exceptions.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 12.
 */
import { Base } from '@exceptions/Base';
import { Code } from '@exceptions/Code';
import { Results } from './Ajax';

export class Exceptions {
    /**
     * 디버그모드 활성화 여부
     */
    static isDebug: boolean = false;

    /**
     * 예외를 처리할 에러핸들러
     */
    static errorHandler: (e: Base, handler: () => Promise<void>) => void = null;

    /**
     * 예외처리 클래스를 초기화한다.
     *
     * @param debug - 디버그모드여부
     */
    static init(debug: boolean): void {
        Exceptions.isDebug = debug;

        window.addEventListener('error', (e) => {
            Exceptions.print(Exceptions.base(e.error));
            e.preventDefault();
            e.stopImmediatePropagation();
        });

        window.addEventListener('unhandledrejection', (e) => {
            Exceptions.print(Exceptions.base(e.reason));
            e.preventDefault();
            e.stopImmediatePropagation();
        });
    }

    /**
     * 일반적인 에러메시지를 가져온다.
     *
     * @param e - 에러객체
     */
    static base(cause: unknown): Base {
        if (cause instanceof Base) {
            return cause;
        } else if (
            typeof cause == 'object' &&
            typeof (cause as any).getMessage == 'function' &&
            typeof (cause as any).getErrors == 'function' &&
            typeof (cause as any).getResponse == 'function'
        ) {
            return cause as Base;
        }

        return new Base(cause);
    }

    /**
     * 에러코드 예외를 가져온다.
     *
     * @param code - 에러코드
     * @param placeholders - 치환자
     * @param footnote - 추가메시지
     * @param errors - 에러데이터
     * @param cause - 에러원인 (자바스크립트 에러인 경우 Error 객체, 서버응답인 경우 Results 객체)
     * @returns 에러코드예외
     */
    static code(
        code: string,
        placeholders: { [key: string]: string } = null,
        footnote: string = null,
        errors: { [key: string]: any } = null,
        cause: Error | Results = null
    ) {
        return new Code(code, placeholders, footnote, errors, cause);
    }

    /**
     * 에러핸들러를 정의한다.
     *
     * @param errorHandler - 에러핸들러
     */
    static setErrorHandler(errorHandler: (e: Base, handler: () => Promise<void>) => void): void {
        this.errorHandler = errorHandler;
    }

    /**
     * 에러를 출력한다.
     *
     * @param e - 에러
     * @param handler - 에러메시지 출력후 처리할 핸들러
     */
    static async print(e: Base, handler: () => Promise<void> = null): Promise<void> {
        if (this.errorHandler !== null) {
            this.errorHandler(e, handler);
        } else {
            /**
             * 디버그모드일때 에러 Stack 을 확인하기 위해 콘솔에 에러메시지를 출력한다.
             */
            Exceptions.error(e);
        }
    }

    /**
     * 콘솔로그 래퍼
     */
    static get error() {
        return Exceptions.isDebug == true ? console.error.bind(console, '[Aui]') : () => {};
    }

    static get warn() {
        return Exceptions.isDebug == true ? console.warn.bind(console, '[Aui]') : () => {};
    }

    static get log() {
        return Exceptions.isDebug == true ? console.log.bind(console, '[Aui]') : () => {};
    }

    static get debug() {
        return Exceptions.isDebug == true ? console.debug.bind(console, '[Aui]') : () => {};
    }

    static get table() {
        return Exceptions.isDebug == true ? console.table.bind(console) : () => {};
    }
}
